import { alpha, SxProps, Theme } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'
import { defaultMargin } from '../home/plChart'

/** Shared visx plot layout — keep in sync with FinancialStatementBarCharts. */
export const STATEMENT_CHART_MARGIN = { ...defaultMargin, top: 16, right: 8, bottom: 8, left: 8 }
export const STATEMENT_CHART_LEFT_GUTTER = 8
export const STATEMENT_CHART_RIGHT_SCALE_WIDTH = 56
export const STATEMENT_CHART_BOTTOM_SCALE_HEIGHT = 28
export const STATEMENT_CHART_BODY_MIN_HEIGHT = 280
export const STATEMENT_CHART_TOOLTIP_GAP = 12
export const STATEMENT_CHART_SCROLL_STEP = 2

export const statementChartScrollButtonSx = (theme: Theme) => ({
    position: 'absolute' as const,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    bgcolor: alpha(theme.palette.background.paper, 0.92),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[1],
    opacity: 0.92,
    '&:hover': {
        bgcolor: theme.palette.action.hover,
    },
    '&.Mui-disabled': {
        opacity: 0.35,
    },
})

/** Tick mark color for visx axes — matches label secondary styling. */
export const getFinancialChartTickStroke = (theme: Theme): string => theme.palette.text.secondary

export const financialSectionTitleSx: SystemStyleObject<Theme> = {
    fontSize: '1.125rem',
    lineHeight: 1.57,
}

export const financialSectionTitleMediumSx: SystemStyleObject<Theme> = {
    ...financialSectionTitleSx,
    fontWeight: 500,
}

export const financialSectionDescriptionSx: SxProps<Theme> = {
    display: 'block',
    mt: 0.01,
    mb: 1.8,
    color: 'text.secondary',
    fontSize: '0.875rem',
    lineHeight: 1.66,
}

export const financialSectionBlockSx: SxProps<Theme> = {
    mt: 3.75,
    width: '100%',
}

export const shareholdingSectionBlockSx: SxProps<Theme> = {
    ...financialSectionBlockSx,
    mt: 4.875,
}

export const financialSectionContentSpacingSx: SystemStyleObject<Theme> = {
    mb: 1.05,
}

export const financialKeyFactLabelSx: SxProps<Theme> = {
    color: 'text.secondary',
    fontSize: 11,
    lineHeight: 1.3,
    textTransform: 'none',
}

export const financialKeyFactValueSx: SxProps<Theme> = {
    fontSize: '16px',
    lineHeight: 1.35,
    '& .MuiTypography-root': {
        fontSize: '16px',
        lineHeight: 1.35,
    },
    '& span': {
        fontSize: '16px',
        lineHeight: 1.35,
    },
}

export const financialChartPanelTitleSx: SxProps<Theme> = {
    fontWeight: 500,
    fontSize: '0.875rem',
    lineHeight: 1.25,
}

export const dualChartRowSx: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    gap: 2,
    width: '100%',
}

export const dualChartPanelSx: SxProps<Theme> = {
    flex: '1 1 400px',
    minWidth: 0,
    maxWidth: '100%',
}

/** Side-by-side panels at 50% width on md+, stacked on xs. */
export const equalDualChartRowSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: 'stretch',
    gap: 2,
    width: '100%',
}

export const equalDualChartPanelSx: SxProps<Theme> = {
    flex: { xs: '1 1 auto', md: '1 1 0' },
    width: { xs: '100%', md: 'calc(50% - 8px)' },
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
}

export const STATEMENT_CHART_PANEL_MIN_HEIGHT = 328
export const VALUATION_PANEL_MIN_HEIGHT = 336

export const financialSectionTabSx = (theme: Theme) => ({
    minHeight: 'auto',
    '& .MuiTab-root': {
        textTransform: 'none',
        fontSize: '15px',
        fontWeight: 500,
        minWidth: 'auto',
        minHeight: 'auto',
        px: 2.1,
        py: 1.2,
        color: theme.palette.text.secondary,
        '&.Mui-selected': {
            color: theme.palette.primary.main,
            fontWeight: 500,
        },
    },
    '& .MuiTabs-indicator': {
        backgroundColor: theme.palette.primary.main,
        height: 3,
    },
})

export const financialSectionA11yProps = (idPrefix: string, index: number) => ({
    id: `${idPrefix}-tab-${index}`,
    'aria-controls': `${idPrefix}-tabpanel-${index}`,
})
